import { useRect } from '@reach/rect';
import React__default, { createContext, useMemo, useEffect, useContext, useRef, useCallback } from 'react';
import styled, { css } from 'styled-components';
import { useIntersectionObserver } from '../../lib/intersection.js';
import { setTestId } from '../../lib/testId.js';
import { Badge } from '../badge/Badge.js';
import { useQueryChildComponents, HappyWebSystemError } from '../base/SystemComponent.js';
import { ButtonIcon } from '../button/ButtonIcon.js';
import { COLOR_PALETTE } from '../colors/colorPalette.js';
import { Icon } from '../icon/Icon.js';
import { Inline } from '../layout/Inline.js';
import { getColor } from '../styles/stylePropTypes.js';
import { Text } from '../text/Text.js';
import { useTableRefs, TableSelectAll } from './Table.js';
import { ScrollHighlightLeft, ScrollHighlightRight } from './TableScrollHighlight.js';

const TableScrollContext = createContext(undefined);
const useTableScroll = ()=>{
    const context = useContext(TableScrollContext);
    if (!context) {
        throw new HappyWebSystemError("useTableScroll", "Missing TableScrollContext - a TableScrollContextProvider must be set.");
    }
    return context;
};
const getScrollToPosition = (scrollToIndex, colWidths, scrollWidth)=>{
    const leftBoundWidth = colWidths[0];
    const rightBoundWidth = colWidths[colWidths.length - 1];
    // The available scroll area, which is the scrollable space between both 'sticky' columns
    const scrollAreaWidth = scrollWidth - leftBoundWidth - rightBoundWidth;
    // Short circuit returning to the 'first' and 'last' columns
    if (scrollToIndex === 1) {
        return 0;
    }
    if (scrollToIndex === colWidths.length - 1) {
        return scrollAreaWidth;
    }
    let scrollToPosition = 0;
    for (const [index, width] of colWidths.entries()){
        if (index !== 0 && index < scrollToIndex) {
            scrollToPosition = scrollToPosition + width;
        }
    }
    return scrollToPosition;
};
const getNextRightIndex = (visibleCols)=>{
    // Default to 'last' scrollable column
    let nextIndex = visibleCols.length - 2;
    let foundVisible = false;
    // Iterate from 'left' to 'right' through scrollable columns until found the visible set, then pick the next
    // 'non-visible' column.
    for (const [index, visible] of visibleCols.entries()){
        if (!foundVisible && visible) {
            foundVisible = true;
        } else if (foundVisible && !visible) {
            nextIndex = index;
            break;
        }
    }
    return nextIndex;
};
const getNextLeftIndex = (visibleCols)=>{
    // Default to 'first' scrollable column
    let nextIndex = 1;
    let foundVisible = false;
    // Iterate from 'right' to 'left' through scrollable columns until found the visible set, then pick the next
    // 'non-visible' column.
    const reverse = [
        ...visibleCols
    ].reverse();
    for (const [index, visible] of reverse.entries()){
        if (!foundVisible && visible) {
            foundVisible = true;
        } else if (foundVisible && !visible) {
            nextIndex = index;
            break;
        }
    }
    // Fix the index from being 'reversed'
    if (nextIndex >= 1) {
        nextIndex = reverse.length - 1 - nextIndex;
    }
    return nextIndex;
};
const TableScrollProvider = ({ containerRef , scrollRef , headerRefs , children  })=>{
    const visibleCols = useRef(new Array(headerRefs.length).fill(false));
    const colWidths = useRef(new Array(headerRefs.length).fill(0));
    /**
     * When observing intersection, the 'sticky' columns need to be ignored as the 'visible' scroll area does not actually
     * include them. Get their width for use in setting the intersection container area.
     */ const leftBoundRect = useRect(headerRefs[0]);
    const rightBoundRect = useRect(headerRefs[headerRefs.length - 1]);
    const observerOptions = useMemo(()=>{
        return {
            containerMargin: `0px -${rightBoundRect?.width ?? 0}px 0px -${leftBoundRect?.width ?? 0}px`,
            containerRef: containerRef,
            ratio: 0.8
        };
    }, [
        containerRef,
        leftBoundRect?.width,
        rightBoundRect?.width
    ]);
    const containerRect = useRect(scrollRef);
    const scrollToLeft = useCallback(()=>{
        const nextIndex = getNextLeftIndex(visibleCols.current);
        const scrollToPosition = getScrollToPosition(nextIndex, colWidths.current, containerRect?.width ?? 0);
        // Keep the current vertical scroll in the same location, horizontally smooth scroll.
        scrollRef.current?.scrollTo(scrollToPosition, scrollRef.current?.scrollTop ?? 0);
    }, [
        containerRect?.width,
        scrollRef
    ]);
    const scrollToRight = useCallback(()=>{
        const nextIndex = getNextRightIndex(visibleCols.current);
        const scrollToPosition = getScrollToPosition(nextIndex, colWidths.current, containerRect?.width ?? 0);
        // Keep the current vertical scroll in the same location, horizontally smooth scroll.
        scrollRef.current?.scrollTo(scrollToPosition, scrollRef.current?.scrollTop ?? 0);
    }, [
        containerRect?.width,
        scrollRef
    ]);
    const value = useMemo(()=>({
            visibleCols,
            colWidths,
            observerOptions,
            scrollToLeft,
            scrollToRight
        }), [
        observerOptions,
        scrollToLeft,
        scrollToRight
    ]);
    return React__default.createElement(TableScrollContext.Provider, {
        value: value
    }, children);
};
const hasTableOverflowMenuChild = (comp)=>{
    return comp?.type?.dataId === TableOverflowMenuHeader.dataId;
};
const TableHeaderRow = ({ children  })=>{
    const refs = useTableRefs();
    const headers = useMemo(()=>{
        const comps = React__default.Children.toArray(children);
        const clonedComps = [];
        for (const [index, child] of comps.entries()){
            // Set the refs for all child components so they can be tracked in parent. Index is shimmed in so that it doesn't
            // need to manually added by a consumer, as all table scroll mechanisms use index as the key.
            clonedComps.push(React__default.cloneElement(child, {
                index,
                forwardedRef: refs.headerRefs[index]
            }));
        }
        // Always add a 'spacer' for the last column in table. Needed to provide scroll controls & scroll highlight.
        if (!hasTableOverflowMenuChild(comps[comps.length - 1])) {
            clonedComps.push(React__default.createElement(TableOverflowMenuHeader, {
                key: comps.length,
                ...{
                    index: comps.length,
                    forwardedRef: refs.headerRefs[comps.length]
                }
            }));
        }
        return clonedComps;
    }, [
        children,
        refs.headerRefs
    ]);
    return React__default.createElement(TableScrollProvider, {
        containerRef: refs.containerRef,
        scrollRef: refs.scrollContainerRef,
        headerRefs: refs.headerRefs
    }, React__default.createElement("thead", null, React__default.createElement("tr", null, headers)));
};
TableHeaderRow.dataId = "data-hpy-web-table-header-row";
TableHeaderRow.displayName = "TableHeaderRow";
const StyledTH = styled.th`
  background-color: ${COLOR_PALETTE.white};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: left;
  padding: 1rem 1.25rem;

  position: sticky;
  top: 0;
  z-index: 3;

  ${(props)=>{
    if (props.stickyY === "none") {
        return "";
    }
    if (props.stickyY === "left") {
        return css`
        left: 0;
        z-index: 4;
      `;
    }
    if (props.stickyY === "right") {
        return css`
        right: 0;
        z-index: 4;
      `;
    }
}}

  /* Allow the scroll buttons to appear */
  &[data-hpy-web-table-overflow-menu-header], &:first-child {
    overflow: visible;
  }
`;
const HeaderScrollHighlightLeft = styled(ScrollHighlightLeft)`
  &::before {
    content: '';
    background: linear-gradient(90deg, ${COLOR_PALETTE.white} 0%, ${COLOR_PALETTE.transparent} 100%);
    width: 1.5rem;
    position: absolute;
    left: 0.25rem;
    height: 100%;
  }
`;
const HeaderScrollHighlightRight = styled(ScrollHighlightRight)`
  &::before {
    content: '';
    background: linear-gradient(270deg, ${COLOR_PALETTE.white} 0%, ${COLOR_PALETTE.transparent} 100%);
    width: 1.5rem;
    position: absolute;
    right: 0.25rem;
    height: 100%;
  }
`;
const ScrollRight = styled.div`
  display: none;
  position: absolute;
  top: 1.5rem;
  left: -0.75rem;
`;
const ScrollLeft = styled.div`
  display: none;
  position: absolute;
  top: 1.5rem;
  right: -0.75rem;
`;
const TableHeaderSortButton = styled.button`
  text-decoration: none;
  white-space: nowrap;
  position: relative;
  background: none;
  border: none;
  border-top: 2px solid transparent;
  cursor: pointer;
  height: 2rem;
  line-height: 2rem;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  padding: 0;
  font-size: 0.75rem;
  font-weight: 600;
  color: ${(props)=>getColor(props.highlight ? "context" : "slateGrey80", props.theme)};
  font-family: inherit;
  text-decoration: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
`;
const TableHeaderScrollButton = styled(ButtonIcon)`
  width: 1.5rem;
  min-width: 1.5rem;
  height: 1.5rem;
  padding: 0;
  z-index: 1;
`;
const TableHeader = ({ align ="left" , stickyY ="none" , forwardedRef , children , ...props })=>{
    const scroll = useTableScroll();
    const header = useIntersectionObserver(forwardedRef, scroll.observerOptions);
    const headerRect = useRect(forwardedRef);
    const sort = props.sort ?? "NONE";
    const supportsSort = props.onSortAsc && props.onSortDesc;
    useEffect(()=>{
        const index = props.index;
        scroll.visibleCols.current[index] = header.intersecting;
        scroll.colWidths.current[index] = headerRect?.width ?? 0;
    // Purposely ignore index as it is set internally by parent
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        header.intersecting,
        headerRect?.width,
        scroll.colWidths,
        scroll.visibleCols
    ]);
    const [others, data] = useQueryChildComponents(children, [
        TableSelectAll,
        TableHeaderSpacer,
        Badge
    ]);
    const [badge, extras] = useQueryChildComponents(others, [
        Badge
    ]);
    if (props.onSortAsc && !props.onSortDesc || !props.onSortDesc && props.onSortDesc) {
        throw new HappyWebSystemError("TableHeader", "Both onSortAsc and onSortDesc must be provided for sort behaviour to work.");
    }
    return React__default.createElement(StyledTH, {
        ref: forwardedRef,
        "data-hpy-web-table-header": true,
        stickyY: stickyY,
        ...props
    }, React__default.createElement(Inline, {
        alignY: "center",
        wrap: false,
        height: "fill",
        width: "fill",
        gap: 1
    }, extras, React__default.createElement(Inline, {
        alignY: "center",
        wrap: false,
        height: "fill",
        width: "fill",
        gap: 0.375,
        align: align
    }, React__default.createElement(Inline, {
        alignY: "center",
        wrap: false,
        height: "fill",
        width: sort === "NONE" && badge.length === 0 ? "fill" : "content",
        gap: 0
    }, data.map((c, i)=>supportsSort ? React__default.createElement(TableHeaderSortButton, {
            key: i,
            highlight: sort !== "NONE",
            onClick: sort === "ASC" ? props.onSortDesc : props.onSortAsc,
            ...setTestId("TableHeaderSortButton-" + c)
        }, data, sort === "ASC" ? React__default.createElement(Icon, {
            size: "small",
            icon: "arrow-up"
        }) : sort === "DESC" ? React__default.createElement(Icon, {
            size: "small",
            icon: "arrow-down"
        }) : null) : typeof c === "string" ? React__default.createElement(Text, {
            key: i,
            color: "slateGrey80",
            size: "small",
            weight: "bold",
            truncate: badge.length === 0
        }, data) : c)), badge)), React__default.createElement(HeaderScrollHighlightLeft, {
        "data-hpy-web-table-scrollhighlight-left": true
    }), React__default.createElement(ScrollLeft, {
        "data-hpy-web-table-scrollhighlight-left": true
    }, React__default.createElement(TableHeaderScrollButton, {
        testId: "Table-Left",
        icon: "caret-left",
        size: "small",
        variant: "context-secondary",
        onClick: scroll.scrollToLeft
    })), React__default.createElement(HeaderScrollHighlightRight, {
        "data-hpy-web-table-scrollhighlight-right": true
    }), React__default.createElement(ScrollRight, {
        "data-hpy-web-table-scrollhighlight-right": true
    }, React__default.createElement(TableHeaderScrollButton, {
        testId: "Table-Right",
        icon: "caret-right",
        size: "small",
        variant: "context-secondary",
        onClick: scroll.scrollToRight
    })));
};
TableHeader.dataId = "data-hpy-web-table-header";
TableHeader.displayName = "TableHeader";
const Spacer = styled.div`
  width: 2.5rem;
  height: 3rem;
`;
const TableHeaderSpacer = ()=>{
    return React__default.createElement(Spacer, {
        "data-hpy-web-table-header-spacer": true
    });
};
TableHeaderSpacer.dataId = "data-hpy-web-table-header-spacer";
TableHeaderSpacer.displayName = "TableHeaderSpacer";
const TableOverflowMenuHeader = (props)=>{
    return React__default.createElement(TableHeader, {
        "data-hpy-web-table-overflow-menu-header": true,
        stickyY: "right",
        ...props
    }, React__default.createElement(TableHeaderSpacer, null));
};
TableOverflowMenuHeader.dataId = "data-hpy-web-table-overflow-menu-header";
TableOverflowMenuHeader.displayName = "TableOverflowMenuHeader";

export { TableHeader, TableHeaderRow, TableHeaderSpacer, TableOverflowMenuHeader };
